import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { entryMotion, prefersReducedMotion } from "./tokens";

type EntryAnimationVariant = "unified" | "sequenced" | "none";

type EntryAnimationStage = "hidden" | "content" | "form" | "cta" | "out";

type UseEntryAnimationOptions = {
  variant?: EntryAnimationVariant;
};

type UseEntryAnimationReturn = {
  stage: EntryAnimationStage;
  liftStyle: CSSProperties;
  contentStyle: CSSProperties;
  formStyle: CSSProperties;
  ctaStyle: CSSProperties;
  animateOut: (callback: () => void) => void;
};

const STAGE_RANK: Record<EntryAnimationStage, number> = {
  hidden: 0,
  content: 1,
  form: 2,
  cta: 3,
  out: 4,
};

const { slideDistance, ctaHeight, easing, out, dimmedContentOpacity } = entryMotion;

const transition = (transformMs: number, opacityMs: number) =>
  `transform ${transformMs}ms ${easing}, opacity ${opacityMs}ms ${easing}`;

/**
 * Web port of mobile useOnboardingAnimation (src/hooks/onboarding).
 *
 * "sequenced": title rises in, then dims to 0.5 while the form rises and the
 * block lifts by the CTA height, then the CTA rises in.
 * "unified": title + form + lift animate together, then the CTA rises in.
 *
 * Durations, distances and easing match the React Native implementation
 * (Animated.timing defaults ≈ ease-in-out).
 */
export const useEntryAnimation = ({
  variant = "unified",
}: UseEntryAnimationOptions = {}): UseEntryAnimationReturn => {
  // A hidden tab never fires rAF, so entrances would stall until focus and
  // timed screens could advance without ever painting. Skip straight to the
  // settled state there – the user isn't watching anyway.
  const [reduced] = useState(
    () =>
      prefersReducedMotion() ||
      variant === "none" ||
      (typeof document !== "undefined" && document.visibilityState === "hidden"),
  );

  const [stage, setStage] = useState<EntryAnimationStage>(reduced ? "cta" : "hidden");
  const timers = useRef<number[]>([]);
  const isAnimatingOut = useRef(false);

  const schedule = useCallback((fn: () => void, delay: number) => {
    timers.current.push(window.setTimeout(fn, delay));
  }, []);

  useEffect(() => {
    if (reduced) return;

    // Double rAF guarantees the "hidden" styles are committed before we
    // transition, so the entrance always plays (StrictMode-safe).
    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        if (variant === "sequenced") {
          setStage("content");
          schedule(() => setStage("form"), entryMotion.content.opacity);
          schedule(() => setStage("cta"), entryMotion.content.opacity + entryMotion.form.opacity);
        } else {
          setStage("form");
          schedule(() => setStage("cta"), entryMotion.contentUnified.opacity);
        }
      });
    });

    const pending = timers.current;
    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      pending.forEach((t) => window.clearTimeout(t));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const animateOut = useCallback(
    (callback: () => void) => {
      if (isAnimatingOut.current) return;
      isAnimatingOut.current = true;

      if (reduced) {
        callback();
        return;
      }

      setStage("out");
      schedule(callback, out + 20);
    },
    [reduced, schedule],
  );

  const styles = useMemo(() => {
    const rank = STAGE_RANK[stage];
    const isOut = stage === "out";
    const none = reduced ? "none" : undefined;

    const contentVisible = rank >= STAGE_RANK.content;
    const contentDimmed = variant === "sequenced" && rank >= STAGE_RANK.form;
    const formVisible = rank >= STAGE_RANK.form;
    const ctaVisible = rank >= STAGE_RANK.cta;
    const lifted = formVisible;

    const contentDurations =
      variant === "sequenced" ? entryMotion.content : entryMotion.contentUnified;

    const liftStyle: CSSProperties = {
      transform: `translateY(${isOut ? -ctaHeight - slideDistance : lifted ? -ctaHeight : 0}px)`,
      transition: none ?? transition(isOut ? out : entryMotion.form.transform, out),
      willChange: "transform",
    };

    const contentStyle: CSSProperties = {
      opacity: isOut ? 0 : contentDimmed ? dimmedContentOpacity : contentVisible ? 1 : 0,
      transform: `translateY(${contentVisible ? 0 : slideDistance}px)`,
      transition:
        none ?? transition(contentDurations.transform, isOut ? out : contentDurations.opacity),
      willChange: "transform, opacity",
    };

    const formStyle: CSSProperties = {
      opacity: isOut ? 0 : formVisible ? 1 : 0,
      transform: `translateY(${formVisible ? 0 : slideDistance}px)`,
      transition:
        none ?? transition(entryMotion.form.transform, isOut ? out : entryMotion.form.opacity),
      willChange: "transform, opacity",
    };

    const ctaStyle: CSSProperties = {
      opacity: isOut ? 0 : ctaVisible ? 1 : 0,
      transform: `translateY(${ctaVisible ? 0 : slideDistance}px)`,
      transition:
        none ??
        transition(entryMotion.button.transform, isOut ? out - 100 : entryMotion.button.opacity),
      willChange: "transform, opacity",
      pointerEvents: ctaVisible && !isOut ? "auto" : "none",
    };

    return { liftStyle, contentStyle, formStyle, ctaStyle };
  }, [stage, variant, reduced]);

  return { stage, ...styles, animateOut };
};
